/* eslint-disable */
// useNotificationHistory — the DRAWER's list. Deliberately a second query.
//
// ⚠️ THIS IS NOT THE BADGE'S SOURCE, AND MUST NEVER BECOME IT.
//   P2a's single-array rule says the badge is the length of ONE array, because a
//   badge fed by a different query than the list it summarises will drift and
//   then the number lies. That rule stands: useNotifications keeps feeding the
//   badge, untouched.
//   The drawer needs something the badge does not — history: read rows, older
//   rows, paged 50 at a time. That is a legitimately different question, so it
//   gets its own query key. The two stay in agreement not by reading each other
//   but because every mutation here invalidates BOTH keys.
//
// ⚠️ SEEN IS NOT READ (spec §2.2).
//   seen_at  = it was on screen         -> does NOT touch the badge
//   read_at  = the person acted on it   -> removes it from the count
//   Opening the drawer stamps seen_at and nothing else. A badge that clears just
//   because a panel opened teaches people the number is decorative.
//
// Paging: explicit .range(), 50 at a time. fetchAllPaged() exists in pgIn.js but
// is the opposite of what is wanted here — it walks the whole table and throws
// past a hard max. This list grows forever (the platform never deletes), so it
// must never be read whole.

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { NOTIFICATIONS_QUERY_KEY } from './useNotifications';
import { usePortalPermissions, canAccessPortal } from './usePortalPermissions';
import { isOrdersRow } from '../components/system/briefing/briefingModel';

export const HISTORY_QUERY_KEY = ['system', 'notifications', 'history'];
export const PAGE_SIZE = 50;

const SELECT = `
  id, notification_id, seen_at, read_at, archived_at, created_at,
  notifications (
    id, type_key, severity, title, body, entity_type, entity_id,
    payload, created_by, created_at,
    notification_types ( key, label_en, portal )
  )
`;

export function useNotificationHistory({ enabled }) {
  const qc = useQueryClient();
  const [pages, setPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = useQuery({
    queryKey: [...HISTORY_QUERY_KEY, pages],
    enabled: !!enabled,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_recipients')
        .select(SELECT)
        .is('archived_at', null)
        .order('created_at', { ascending: false })
        .range(0, pages * PAGE_SIZE - 1);
      if (error) throw error;
      return data || [];
    },
  });

  // Orders alerts are for people authorised for Orders only (fail-closed while permissions load).
  const perms = usePortalPermissions();
  const ordersAllowed = !perms.isLoading && canAccessPortal(perms.data, 'orders');
  const rows = (query.data || []).filter((r) => r.notifications && (ordersAllowed || !isOrdersRow(r)));
  // A full page back means there is probably more behind it.
  const hasMore = (query.data || []).length >= pages * PAGE_SIZE;

  // Both keys, every time. This is what keeps the drawer and the badge agreeing
  // without either one reading the other.
  const invalidateBoth = useCallback(() => {
    qc.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
    qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
  }, [qc]);

  const fail = (err, where) =>
    Sentry.captureException(err, { tags: { source: `useNotificationHistory.${where}` } });

  // seen_at ONLY. Never read_at. Called once when the drawer opens.
  const markSeen = useCallback(async (ids) => {
    if (!ids?.length) return;
    const { error } = await supabase
      .from('notification_recipients')
      .update({ seen_at: new Date().toISOString() })
      .in('id', ids)
      .is('seen_at', null);
    if (error) { fail(error, 'markSeen'); return; }
    // Deliberately invalidates only the drawer: seen_at cannot change the badge,
    // so refetching the badge's query here would be noise.
    qc.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
  }, [qc]);

  const markRead = useCallback(async (id) => {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
      .is('read_at', null);          // already-read rows are a no-op, never a toggle
    if (error) { fail(error, 'markRead'); return; }
    invalidateBoth();
  }, [invalidateBoth]);

  const markAllRead = useCallback(async () => {
    const unread = rows.filter((r) => !r.read_at).map((r) => r.id);
    if (!unread.length) return;
    const { error } = await supabase
      .from('notification_recipients')
      .update({ read_at: new Date().toISOString() })
      .in('id', unread);
    if (error) { fail(error, 'markAllRead'); return; }
    invalidateBoth();
  }, [rows, invalidateBoth]);

  // Nothing is ever deleted — archived_at is a stamp and the row stays in the
  // database. There is no archived view in P4, which is why archive offers an
  // undo (spec §5.5).
  const archive = useCallback(async (id) => {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { fail(error, 'archive'); return; }
    invalidateBoth();
  }, [invalidateBoth]);

  const unarchive = useCallback(async (id) => {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ archived_at: null })
      .eq('id', id);
    if (error) { fail(error, 'unarchive'); return; }
    invalidateBoth();
  }, [invalidateBoth]);

  const loadOlder = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setPages((p) => p + 1);
    // the query refetches on the key change; release the guard once it settles
    setTimeout(() => setLoadingMore(false), 600);
  }, [loadingMore, hasMore]);

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    retry: () => qc.invalidateQueries({ queryKey: HISTORY_QUERY_KEY }),
    hasMore,
    loadingMore,
    loadOlder,
    markSeen,
    markRead,
    markAllRead,
    archive,
    unarchive,
  };
}
